import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Printer, Download, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { sanitizeSvg } from '../lib/sanitizeSvg';
import { brandName, brandEmail, brandPhone } from '../lib/brand';

/**
 * ColorSeparationSheet
 * --------------------
 * Renders a 17" × 36" film-ready screen-print separation sheet for an
 * item whose `vector_design.format === 'svg'`. One channel per spot color,
 * each shown in film-positive convention (black ink on white background).
 *
 * Halftone-eligible channels (the API auto-flags any SVG containing a
 * gradient/raster/pattern element) are tagged with a yellow badge so the
 * production team knows to output halftone film instead of a solid film
 * positive for that channel.
 *
 * The "art board" is the SVG itself — when the user clicks Download, we
 * serialize the rendered DOM node as an SVG file (vector — opens in
 * Illustrator) and as a one-page PDF via printing.
 */
const PX_PER_INCH = 30;   // on-screen scale (510 × 1080 preview)
const ART_W_IN = 17;
const ART_H_IN = 36;

const ColorSeparationSheet = ({ open, onClose, proposalId, itemIndex }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const sheetRef = useRef(null);

  // Lazy-fetch separations when the modal opens. We use a guarded
  // refetch helper so React Strict Mode's double-invoke + dependency
  // changes don't trigger overlapping requests.
  useEffect(() => {
    if (!open || !proposalId || itemIndex == null) return undefined;
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `/proposals/${proposalId}/items/${itemIndex}/color-separations`
        );
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Could not build separations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [open, proposalId, itemIndex]);

  const channels = data?.separations || [];
  const channelCount = channels.length;

  // Layout: header (3"), locations strip (5"), then channels split the rest.
  const HEADER_H = 3 * PX_PER_INCH;
  const LOC_H = 5 * PX_PER_INCH;
  const remaining = ART_H_IN - 3 - 5; // 28"
  const channelH = channelCount > 0 ? (remaining * PX_PER_INCH) / channelCount : 0;

  const today = new Date().toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  // Build a single self-contained SVG for download — wraps the sheet ref
  // contents so Illustrator opens it cleanly.
  const handleDownloadSvg = () => {
    if (!sheetRef.current) return;
    const node = sheetRef.current.querySelector('svg');
    if (!node) return;
    const blob = new Blob([node.outerHTML], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `separations-${data?.proposal_number || 'design'}-item${itemIndex + 1}.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!sheetRef.current) return;
    // Pop the sheet into a fresh window so the user's whole page chrome
    // doesn't print. Keeps Cmd-P / Ctrl-P working as "Save as PDF".
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) {
      toast.error('Pop-up blocked — allow pop-ups to print');
      return;
    }
    const svgHtml = sheetRef.current.querySelector('svg').outerHTML;
    w.document.write(`<!doctype html><html><head><title>Separations · #${data?.proposal_number || ''}</title>
      <style>
        @page { size: ${ART_W_IN}in ${ART_H_IN}in; margin: 0; }
        body { margin: 0; background: white; }
        svg { width: ${ART_W_IN}in; height: ${ART_H_IN}in; }
      </style>
    </head><body>${svgHtml}<script>window.onload=()=>{window.print();setTimeout(()=>window.close(),200);}</script></body></html>`);
    w.document.close();
  };

  // Pre-compose location size badges (one per location with a real size).
  const locationBadges = useMemo(() => {
    if (!data) return [];
    const locs = data.print_locations || [];
    const sizes = data.print_sizes || {};
    return locs.map((loc) => {
      const key = String(loc).toLowerCase().replace(/\s+/g, '_');
      return {
        loc: String(loc).replace(/_/g, ' '),
        size: sizes[key] || sizes[String(loc).toLowerCase()] || '—',
      };
    });
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto bg-gray-100" data-testid="color-sep-modal">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle className="text-base">Screen-Print Color Separations</DialogTitle>
              <p className="text-xs text-gray-600 mt-0.5">
                Film-ready art board · 17″ × 36″ · {channelCount} color{channelCount === 1 ? '' : 's'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleDownloadSvg} disabled={!data || downloading} data-testid="color-sep-download-svg">
                <Download size={14} className="mr-1" /> SVG
              </Button>
              <Button size="sm" variant="outline" onClick={handlePrint} disabled={!data || downloading} data-testid="color-sep-print">
                <Printer size={14} className="mr-1" /> Print / PDF
              </Button>
              <Button size="sm" variant="ghost" onClick={onClose} data-testid="color-sep-close">
                <X size={14} />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-gray-500 gap-2">
            <Loader2 size={16} className="animate-spin" />
            Building separations…
          </div>
        ) : !data ? (
          <div className="py-20 text-center text-gray-500 text-sm">No data.</div>
        ) : (
          <div
            ref={sheetRef}
            className="flex justify-center mt-2"
            data-testid="color-sep-sheet"
          >
            {/* The 17×36 SVG art board. We render at PX_PER_INCH so the
                on-screen preview is the same proportion as the eventual
                print, and the SVG download/print produces a perfectly
                scaled artifact. */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={ART_W_IN * PX_PER_INCH}
              height={ART_H_IN * PX_PER_INCH}
              viewBox={`0 0 ${ART_W_IN * PX_PER_INCH} ${ART_H_IN * PX_PER_INCH}`}
              style={{
                background: '#ffffff',
                boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
              }}
            >
              {/* Crop marks at each corner — production convention */}
              {[[0,0],[ART_W_IN,0],[0,ART_H_IN],[ART_W_IN,ART_H_IN]].map(([x,y], i) => (
                <g key={i} stroke="#000" strokeWidth="0.5">
                  <line
                    x1={x * PX_PER_INCH - 12}
                    y1={y * PX_PER_INCH}
                    x2={x * PX_PER_INCH + 12}
                    y2={y * PX_PER_INCH}
                  />
                  <line
                    x1={x * PX_PER_INCH}
                    y1={y * PX_PER_INCH - 12}
                    x2={x * PX_PER_INCH}
                    y2={y * PX_PER_INCH + 12}
                  />
                </g>
              ))}

              {/* ===== HEADER ===== */}
              <g>
                <text x={20} y={28} fontSize="20" fontWeight="900" fontFamily="Arial">
                  STARES GROUP
                </text>
                <text x={20} y={48} fontSize="12" fontFamily="Arial" fill="#333">
                  Color Separation Sheet · Film-Ready
                </text>
                <text x={ART_W_IN * PX_PER_INCH - 20} y={28} fontSize="11" fontFamily="Arial" textAnchor="end">
                  PROPOSAL #{data.proposal_number}
                </text>
                <text x={ART_W_IN * PX_PER_INCH - 20} y={46} fontSize="10" fontFamily="Arial" textAnchor="end" fill="#444">
                  {data.customer_name} · {today}
                </text>
                <text x={20} y={70} fontSize="10" fontFamily="Arial" fill="#444">
                  Style: {data.style_number || '—'} · Color: {data.color || '—'} · Qty: {data.quantity || '—'} · Method: SCREEN PRINT
                </text>
                <line
                  x1={20} y1={HEADER_H - 18}
                  x2={ART_W_IN * PX_PER_INCH - 20} y2={HEADER_H - 18}
                  stroke="#000" strokeWidth="1"
                />
                <text x={20} y={HEADER_H - 4} fontSize="11" fontWeight="700" fontFamily="Arial">
                  PRINT LOCATIONS &amp; SIZES
                </text>
              </g>

              {/* ===== LOCATIONS STRIP — thumbnail of full design + size badges ===== */}
              <g transform={`translate(0, ${HEADER_H})`}>
                {/* Centered full-color design */}
                <foreignObject
                  x={20}
                  y={4}
                  width={LOC_H - 30}
                  height={LOC_H - 30}
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{ width: '100%', height: '100%' }}
                    dangerouslySetInnerHTML={{ __html: sanitizeSvg(data.original_svg) }}
                  />
                </foreignObject>
                {/* Right side: per-location badges */}
                <g transform={`translate(${LOC_H + 10}, 12)`}>
                  {locationBadges.length === 0 && (
                    <text fontSize="11" fill="#666">
                      No print locations recorded.
                    </text>
                  )}
                  {locationBadges.map((b, idx) => (
                    <g key={idx} transform={`translate(0, ${idx * 32})`}>
                      <rect
                        x={0} y={0} width={ART_W_IN * PX_PER_INCH - LOC_H - 40}
                        height={26}
                        fill="#fff" stroke="#000" strokeWidth="0.75"
                      />
                      <text x={10} y={17} fontSize="11" fontWeight="700" fontFamily="Arial">
                        {b.loc.toUpperCase()}
                      </text>
                      <text x={140} y={17} fontSize="11" fontFamily="Arial">
                        Print size: {b.size}
                      </text>
                    </g>
                  ))}
                </g>
                <line
                  x1={20} y1={LOC_H - 4}
                  x2={ART_W_IN * PX_PER_INCH - 20} y2={LOC_H - 4}
                  stroke="#000" strokeWidth="1"
                />
              </g>

              {/* ===== CHANNELS ===== */}
              {channels.map((ch, i) => {
                const top = HEADER_H + LOC_H + i * channelH;
                const designBoxW = channelH * 1.4; // wide design
                const margin = 24;
                return (
                  <g key={i} transform={`translate(0, ${top})`}>
                    {/* Channel header */}
                    <text x={margin} y={22} fontSize="13" fontWeight="900" fontFamily="Arial">
                      COLOR {i + 1} of {channelCount}
                    </text>
                    <rect
                      x={margin + 140} y={6}
                      width={22} height={22}
                      fill={ch.color} stroke="#000" strokeWidth="0.75"
                    />
                    <text x={margin + 170} y={22} fontSize="12" fontFamily="monospace">
                      {ch.color.toUpperCase()}
                    </text>
                    <text x={margin + 250} y={22} fontSize="12" fontFamily="Arial" fontWeight="700">
                      {ch.ink_name || '—'}
                    </text>
                    {ch.halftone && (
                      <g>
                        <rect
                          x={margin + 430} y={6}
                          width={140} height={22}
                          fill="#fff7ed" stroke="#ea580c" strokeWidth="0.75"
                        />
                        <text x={margin + 436} y={22} fontSize="11" fontWeight="700" fontFamily="Arial" fill="#9a3412">
                          HALFTONE · 45 LPI 22.5°
                        </text>
                      </g>
                    )}
                    {/* Film-ready isolated channel — black on white */}
                    <foreignObject
                      x={margin}
                      y={32}
                      width={designBoxW}
                      height={channelH - 44}
                    >
                      <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        style={{
                          width: '100%',
                          height: '100%',
                          background: '#fff',
                          border: '1px solid #000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 6,
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeSvg(ch.svg) }}
                      />
                    </foreignObject>
                    {/* Right side notes column — kept tight so it stays
                        within channelH (~168px) even for 5+ channels. */}
                    <g transform={`translate(${margin + designBoxW + 20}, 36)`}>
                      <text fontSize="10" fontFamily="Arial" fontWeight="700">SCREEN SPECS</text>
                      <text y={14} fontSize="9" fontFamily="Arial">Mesh: 156 (solid) / 230 (halftone)</text>
                      <text y={26} fontSize="9" fontFamily="Arial">Emulsion: dual-cure · Squeegee 70 dur</text>
                      <text y={38} fontSize="9" fontFamily="Arial">Off-contrast: 1/16&quot; · medium pressure</text>
                      <text y={58} fontSize="10" fontFamily="Arial" fontWeight="700">INK / THREAD</text>
                      {/* Logo vs Print color chips */}
                      <g transform="translate(0, 64)">
                        <rect x={0} y={0} width={14} height={14} fill={ch.color} stroke="#000" strokeWidth="0.4" />
                        <text x={20} y={11} fontSize="9" fontFamily="Arial">
                          Logo {ch.color.toUpperCase()}
                        </text>
                      </g>
                      {ch.victory_hex && (
                        <g transform="translate(0, 82)">
                          <rect x={0} y={0} width={14} height={14} fill={ch.victory_hex} stroke="#000" strokeWidth="0.4" />
                          <text x={20} y={11} fontSize="9" fontFamily="Arial">
                            Print {ch.ink_name}
                          </text>
                        </g>
                      )}
                      {ch.pms && (
                        <text y={114} fontSize="10" fontFamily="Arial" fontWeight="700">
                          PMS {ch.pms}
                        </text>
                      )}
                      {ch.thread && (
                        <text y={128} fontSize="9" fontFamily="Arial">
                          Madeira Thread {ch.thread}
                        </text>
                      )}
                      {ch.halftone && (
                        <text y={144} fontSize="9" fontFamily="Arial" fill="#9a3412">
                          Halftone film · 45 LPI · 22.5°
                        </text>
                      )}
                    </g>
                    {i < channelCount - 1 && (
                      <line
                        x1={20} y1={channelH - 2}
                        x2={ART_W_IN * PX_PER_INCH - 20} y2={channelH - 2}
                        stroke="#000" strokeWidth="0.5" strokeDasharray="4 2"
                      />
                    )}
                  </g>
                );
              })}

              {/* ===== FOOTER ===== */}
              <g transform={`translate(0, ${ART_H_IN * PX_PER_INCH - 22})`}>
                <line
                  x1={20} y1={0}
                  x2={ART_W_IN * PX_PER_INCH - 20} y2={0}
                  stroke="#000" strokeWidth="0.5"
                />
                <text x={20} y={14} fontSize="9" fontFamily="Arial" fill="#666">
                  Generated by {brandName()} Proposal Builder · {today}
                </text>
                <text x={ART_W_IN * PX_PER_INCH - 20} y={14} fontSize="9" fontFamily="Arial" fill="#666" textAnchor="end">
                  Item #{itemIndex + 1} · {channelCount} ink{channelCount === 1 ? '' : 's'}
                </text>
              </g>
            </svg>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ColorSeparationSheet;
